import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

import { AppPageHeader } from '@/components/layout/AppPageHeader.comp.tsx';
import { TopBarAnnouncementsButton } from '@/components/layout/topBar/components/announcementsButton/TopBarAnnouncementsButton.comp.tsx';
import { TopBarFeedbackButton } from '@/components/layout/topBar/components/feedbackButton/TopBarFeedbackButton.comp.tsx';
import {
  TopBarItemsStyles,
  TopBarProfileAvatarStyles,
  TopBarProfileButtonStyles,
  TopBarProfileMenuDividerStyles,
  TopBarProfileMenuEmailStyles,
  TopBarProfileMenuHeaderStyles,
  TopBarProfileMenuItemStyles,
  TopBarProfileMenuListStyles,
  TopBarProfileMenuNameStyles,
  TopBarProfileMenuPaperStyles,
  TopBarProfileNameStyles,
  TopBarProfileRoleChipStyles,
  TopBarStyles,
} from '@/components/layout/topBar/TopBar.styles.ts';
import { useLogout } from '@/components/layout/topBar/util/useLogout.util.ts';
import { ViewDivider } from '@/components/ViewDivider.comp.tsx';
import { CONFIG } from '@/config/config.ts';
import { themeColors } from '@/config/theme/themeColors.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

import { Logo } from '../logo/Logo.comp';

export const TopBar: React.FC = () => {
  const { t } = useTranslationWithPrefix('topBar');
  const { isLoggingOut, logout } = useLogout();
  const { email, firstName, lastName, role } = useUserContext();
  const [profileAnchorEl, setProfileAnchorEl] = useState<HTMLElement | null>(null);
  const isProfileMenuOpen = Boolean(profileAnchorEl);
  const profileName = [firstName.trim(), lastName.trim().slice(0, 2)].filter(Boolean).join(' ');
  const profileInitials = [firstName, lastName]
    .map(name => name.trim().charAt(0))
    .filter(Boolean)
    .join('')
    .toUpperCase();

  const handleLogout = async () => {
    setProfileAnchorEl(null);
    await logout();
  };

  const handleProfileMenuClose = () => {
    setProfileAnchorEl(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setProfileAnchorEl(event.currentTarget);
  };

  return (
    <>
      <Stack direction="row" sx={TopBarStyles}>
        <Stack direction="row" spacing={4} sx={{ alignItems: 'center' }} useFlexGap>
          <Logo />
          <AppPageHeader />
        </Stack>
        <Stack direction="row" spacing={3} sx={TopBarItemsStyles}>
          {!CONFIG.IS_PROD && <Box sx={{ color: themeColors.greyDark }}>{CONFIG.ENVIRONMENT}</Box>}
          <TopBarAnnouncementsButton />
          <TopBarFeedbackButton />
          <Button
            aria-controls={isProfileMenuOpen ? 'topbar-profile-menu' : undefined}
            aria-expanded={isProfileMenuOpen ? 'true' : undefined}
            aria-haspopup="menu"
            aria-label={t('tooltip.profile')}
            disabled={isLoggingOut}
            endIcon={<KeyboardArrowDownIcon />}
            id="topbar-profile-button"
            onClick={handleProfileMenuOpen}
            startIcon={<Avatar sx={TopBarProfileAvatarStyles}>{profileInitials}</Avatar>}
            sx={TopBarProfileButtonStyles}
            variant="outlined"
          >
            <Box component="span" sx={TopBarProfileNameStyles}>
              {profileName}
            </Box>
          </Button>
          <Menu
            anchorEl={profileAnchorEl}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            id="topbar-profile-menu"
            onClose={handleProfileMenuClose}
            open={isProfileMenuOpen}
            slotProps={{
              list: {
                'aria-labelledby': 'topbar-profile-button',
                sx: TopBarProfileMenuListStyles,
              },
              paper: { sx: TopBarProfileMenuPaperStyles },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          >
            <Box sx={TopBarProfileMenuHeaderStyles}>
              <Typography sx={TopBarProfileMenuNameStyles}>{profileName}</Typography>
              <Typography sx={TopBarProfileMenuEmailStyles}>{email}</Typography>
              {role && <Chip label={role.name} size="small" sx={TopBarProfileRoleChipStyles} />}
            </Box>
            <Divider sx={TopBarProfileMenuDividerStyles} />
            <MenuItem
              disabled={isLoggingOut}
              onClick={handleLogout}
              sx={TopBarProfileMenuItemStyles}
            >
              {t('tooltip.logout')}
            </MenuItem>
          </Menu>
        </Stack>
      </Stack>
      <ViewDivider />
    </>
  );
};
